import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Card,
  Button,
  Accordion,
  Form,
  Row,
  Col,
} from "react-bootstrap";
import Select from "react-select";
import Pagination from "../../components/Pagination";
import useClientTableSort, {
  sortRowsByConfig,
} from "../../hooks/useClientTableSort";
import useAccess from "../../hooks/useAccess";

const RoadsideInspectionList = () => {
  const [records, setRecords] = useState([]);
  const [recordsUnfiltered, setRecordsUnfiltered] = useState([]);
  const [filterDate, setFilterDate] = useState("");
  const [filterSavedBy, setFilterSavedBy] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const tableRef = useRef(null);
  const itemsPerPage = 20;

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const { hasPermission } = useAccess();
  const userId = isAdmin && paramId ? paramId : localStorage.getItem("userId");
  const canDeleteRecords = isAdmin && hasPermission("Task", "Task", "delete");

  useEffect(() => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };
    axios
      .get(`${API_BASE}/api/roadside/${userId}`, { headers })
      .then((res) => {
        setRecordsUnfiltered(res.data || []);
        setRecords(res.data || []);
      })
      .catch((err) => console.error("Fetch roadside inspections", err));
  }, [API_BASE, userId]);

  const applyFilters = () => {
    let filtered = [...recordsUnfiltered];

    if (filterDate) {
      filtered = filtered.filter(
        (i) =>
          i.ri_inspection_date &&
          new Date(i.ri_inspection_date).toDateString() ===
            new Date(filterDate).toDateString()
      );
    }

    if (filterSavedBy?.length) {
      const selectedUsers = filterSavedBy.map((s) => s.value);
      filtered = filtered.filter((i) =>
        selectedUsers.includes(i.saved_by_name)
      );
    }

    setRecords(filtered);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterDate("");
    setFilterSavedBy(null);
    setRecords(recordsUnfiltered);
  };

  const savedByOptions = [
    ...new Set(recordsUnfiltered.map((i) => i.saved_by_name).filter(Boolean)),
  ].map((s) => ({ label: s, value: s }));

  const getViolationCount = (record) => {
    if (Array.isArray(record.ri_violations)) return record.ri_violations.length;
    try {
      return JSON.parse(record.ri_violations || "[]").length;
    } catch {
      return 0;
    }
  };

  const sortConfig = useClientTableSort(tableRef, () => setCurrentPage(1));
  const sortedRecords = sortRowsByConfig(records, sortConfig, [
    (record) => (record.ri_is_clean ? "Clean" : "Action"),
    (record) => record.ri_dot || "-",
    (record) => (record.ri_dot_flag ? "Yes" : "No"),
    (record) => record.ri_inspection_date,
    (record) => record.ri_inspection_level || "-",
    getViolationCount,
    (record) => record.saved_by_name || "-",
    (record) => record.ri_saved_date,
  ]);

  const paginatedRecords = sortedRecords.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const goBack = () => {
    if (isAdmin && paramId) navigate(`/userhome/${paramId}`);
    else navigate("/userhome");
  };

  const handleDeleteRecord = async (recordId) => {
    if (!canDeleteRecords || !recordId) return;
    if (!window.confirm("Delete this record? This cannot be undone.")) return;

    try {
      await axios.delete(`${API_BASE}/api/roadside/${recordId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setRecords((prev) => prev.filter((row) => row.ri_id !== recordId));
      setRecordsUnfiltered((prev) => prev.filter((row) => row.ri_id !== recordId));
    } catch (err) {
      console.error("Roadside delete failed", err);
      alert(err?.response?.data?.message || "Failed to delete record.");
    }
  };

  return (
    <Container className="form-wrapper my-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">Roadside Inspections</h3>
        <div>
          <Button variant="secondary" className="me-2" onClick={goBack}>
            Return
          </Button>
          <Button
            variant="success"
            onClick={() => navigate(`/roadside/${userId}/new`)}
          >
            Add
          </Button>
        </div>
      </div>

      <Card>
        <Card.Body>
          <Accordion defaultActiveKey="">
            <Accordion.Item eventKey="0">
              <Accordion.Header>Filters</Accordion.Header>
              <Accordion.Body>
                <Form className="mb-4">
                  <Row className="align-items-end g-3">
                    <Col md={3}>
                      <Form.Label>Inspection Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={filterDate}
                        onChange={(e) => setFilterDate(e.target.value)}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Saved By</Form.Label>
                      <Select
                        isMulti
                        options={savedByOptions}
                        value={filterSavedBy}
                        onChange={setFilterSavedBy}
                        placeholder="Select Saved By"
                      />
                    </Col>
                    <Col md={3} className="d-flex gap-2">
                      <Button
                        variant="secondary"
                        onClick={clearFilters}
                        className="mt-3"
                      >
                        Clear Filter
                      </Button>
                      <Button
                        variant="success"
                        onClick={applyFilters}
                        className="mt-3"
                      >
                        Filter
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {records.length === 0 ? (
            <p className="mt-3 text-muted fst-italic">
              No roadside inspections found.
            </p>
          ) : (
            <div className="table-responsive mt-3">
              <table
                ref={tableRef}
                className="table table-bordered align-middle text-center"
                data-nw-client-sort="true"
              >
                <thead className="table-light">
                  <tr>
                    <th>Status</th>
                    <th>TR #</th>
                    <th>DOT</th>
                    <th>Inspection Date</th>
                    <th>Inspection Level</th>
                    <th>Violations</th>
                    <th>Last Updated Name</th>
                    <th>Last Updated Date</th>
                    <th className="text-center" style={{ width: "110px" }}>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRecords.map((r) => {
                    const violCount = getViolationCount(r);

                    return (
                      <tr key={r.ri_id}>
                        <td>{r.ri_is_clean ? "Clean" : "Action"}</td>
                        <td>{r.ri_dot || "-"}</td>
                        <td>{r.ri_dot_flag ? "Yes" : "No"}</td>
                        <td>
                          {r.ri_inspection_date
                            ? new Date(
                                r.ri_inspection_date
                              ).toLocaleDateString()
                            : "-"}
                        </td>
                        <td>{r.ri_inspection_level || "-"}</td>
                        <td>{violCount}</td>
                        <td>{r.saved_by_name || "-"}</td>
                        <td>
                          {r.ri_saved_date
                            ? new Date(r.ri_saved_date).toLocaleDateString()
                            : "-"}
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() =>
                                navigate(`/roadside/${userId}/detail/${r.ri_id}`)
                              }
                            >
                              Edit
                            </Button>
                            {canDeleteRecords && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteRecord(r.ri_id)}
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <Pagination
                currentPage={currentPage}
                totalItems={records.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default RoadsideInspectionList;
